import { useMemo, useContext } from 'react'
import { TableContext } from 'Context/Table'

import CustomHeaderColumn from './Components/CustomHeaderColumn'
import CustomRow from './Components/CustomRow'
import CustomTablePagination from './Components/CustomTablePagination'
import TableLoading from './Components/TableLoading'
import TableError from './Components/TableError'

import {
  useTable,
  useSortBy,
  useFilters,
  usePagination,
  Row,
  TableInstance,
  UsePaginationInstanceProps,
} from 'react-table'

import { TableProps } from 'Types/Presentation/Components/Table'

type ReactTableProps = TableInstance & UsePaginationInstanceProps<{}>

const ClientSideTable: React.FC<TableProps> = ({
  data,
  columns,
  items_per_page,
  rowKey,
  onClickRow,
  isGrey,
  sorting,
  autoResetPage = true,
}) => {
  const { isLoading, isError, currentSorting, pagination } = useContext(TableContext)
  const isShowLoading = useMemo(() => {
    if (isLoading !== undefined) {
      return isLoading
    } else {
      return false
    }
  }, [isLoading])
  const memorizedColumns = useMemo(() => columns, [columns])

  const {
    getTableProps,
    getTableBodyProps,
    headers,
    gotoPage,
    page,
    pageOptions,
    prepareRow,
    state,
    headerGroups,
  } = useTable(
    {
      columns: memorizedColumns,
      data,
      initialState: {
        pageSize: items_per_page,
        sortBy: sorting ?? currentSorting,
      } as any,
      autoResetPage,
    } as any,
    useFilters,
    useSortBy,
    usePagination
  ) as ReactTableProps

  const { pageIndex } = state as any

  const gotoPageClientSide = (page: number) => {
    gotoPage(page - 1)
  }
  return (
    <div className='position-relative'>
      <div className='position-relative'>
        <div className='table-responsive pb-20 pb-lg-10'>
          <div className='tableWrap' style={{ overflowX: 'auto' }}>
            <div className='mobile-sort'>
              {headerGroups.map((headerGroup: any) => {
                const { key, ...headerGroupProps } = headerGroup.getHeaderGroupProps()
                const randomId = Math.random().toString(36).substring(2, 15)
                return (
                  <div key={headerGroup.id} className='sort-wrapper' {...headerGroupProps}>
                    {headerGroup.headers.map((column: any, idx: number) => {
                      const { key: columnKey, ...columnProps } = column.getHeaderProps(
                        column.getSortByToggleProps()
                      )
                      const isSortable = column.canSort && !column.disableSortBy

                      return (
                        <div
                          key={`column-${idx}-${randomId}`}
                          scope='col'
                          className={isSortable ? 'table-header-sortable cursor-pointer' : ''}
                          {...columnProps}>
                          <span className={isSortable ? 'table-header-sortable-label' : ''}>
                            {column.render('Header')}
                          </span>
                          {/* Add a sort direction indicator */}
                          <span>{column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}</span>
                        </div>
                      )
                    })}
                  </div>
                )
              })}
            </div>
            <table
              className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'
              {...getTableProps()}>
              <thead>
                <tr className='text-start text-muted fs-7 text-uppercase gs-0'>
                  {headers.map((column: any, idx) => {
                    let className: React.CSSProperties = {
                      color: '#475467',
                      textTransform: 'none',
                      // backgroundColor: theme === 'light' ? '#eee' : '#151521',
                    }

                    className = {
                      ...className,
                      paddingLeft: '1.25rem',
                      paddingRight: '1.25rem',
                      width: column.width,
                      minWidth: column.minWidth,
                      maxWidth: column.maxWidth,
                    }

                    return (
                      <CustomHeaderColumn
                        key={column.id}
                        headerRight={column.headerRight}
                        column={column}
                        additionalStyles={className}
                      />
                    )
                  })}
                </tr>
              </thead>
              <tbody className='text-black px-5' {...getTableBodyProps()}>
                {page.length > 0 ? (
                  page.map((row: Row, i) => {
                    prepareRow(row)

                    return (
                      <CustomRow
                        row={row}
                        rowKey={rowKey}
                        onClickRow={onClickRow}
                        isGrey={isGrey && i % 2 === 0}
                        key={`row-${i}-${row.id}`}
                      />
                    )
                  })
                ) : (
                  <tr>
                    <td colSpan={7}>
                      <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                        {!isError ? 'No matching records found' : ''}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {isShowLoading && <TableLoading />}
      </div>

      {page.length > 0 && pagination && (
        <CustomTablePagination
          isLoading={isLoading}
          totalCountData={data.length}
          page={pageIndex + 1}
          items_per_page={items_per_page}
          updatePage={gotoPageClientSide}
          links={[...Array(pageOptions.length)].map((_, idx) => ({
            label: `${idx + 1}`,
            active: idx + 1 === pageIndex + 1,
            page: idx + 1,
            url: null,
          }))}
        />
      )}

      {isError && <TableError />}
    </div>
  )
}

export default ClientSideTable
